import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SectionCard, EmptyState, Pill, ActionButton } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import CanonicalUploader from "@/components/documents/CanonicalUploader";

const SOURCES = [
  { key: "email", label: "Email thread", icon: "Mail", hint: "Full email chain with From/To/Subject/Date headers" },
  { key: "gmail", label: "Gmail copy/paste", icon: "Mail", hint: "Pasted from Gmail (\"On … wrote:\" style)" },
  { key: "outlook", label: "Outlook copy/paste", icon: "Mail", hint: "Pasted from Outlook (\"-----Original Message-----\")" },
  { key: "plain_text", label: "Plain text", icon: "FileText", hint: "Unstructured correspondence or notes" },
  { key: "portal_export", label: "Exported correspondence", icon: "FileDown", hint: "Portal or other exported thread" },
];
const SOURCE_LABEL = Object.fromEntries(SOURCES.map((s) => [s.key, s.label]));
const PARSE_TONE = { parsed: "success", partially_parsed: "warning", unstructured: "neutral" };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const fmtDT = (d) => (d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

/* ───────────────────────── Import Wizard ───────────────────────── */
function ImportWizard({ clientId, onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [sourceType, setSourceType] = useState("email");
  const [raw, setRaw] = useState("");
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const [convDate, setConvDate] = useState("");
  const [convTime, setConvTime] = useState("");
  const [dateUnknown, setDateUnknown] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const submittedRef = useRef(false);
  const fileRef = useRef(null);

  const runPreview = async () => {
    if (!raw.trim()) return toast.error("Paste or upload the conversation first.");
    if (sourceType === "plain_text" && !dateUnknown && !convDate) {
      return toast.error("Conversation Date is required. Allison relies on chronology to determine client intent, conversation history, and the correct next workflow step.");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/historical-conversations/preview", {
        source_type: sourceType, client_id: clientId, raw_content: raw,
        conversation_date: dateUnknown ? undefined : convDate || undefined,
        conversation_time: dateUnknown ? undefined : convTime || undefined,
        date_unknown: dateUnknown || undefined,
      });
      setPreview(data); setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not parse the content."); }
    finally { setBusy(false); }
  };

  const doImport = async (force = false) => {
    if (submittedRef.current) return;              // guard against double submit
    submittedRef.current = true; setBusy(true);
    try {
      const tagArr = tags.split(",").map((t) => t.trim()).filter(Boolean);
      const { data } = await api.post("/historical-conversations", {
        source_type: sourceType, client_id: clientId, raw_content: raw, title: title.trim() || undefined, tags: tagArr, force,
        conversation_date: dateUnknown ? undefined : convDate || undefined,
        conversation_time: dateUnknown ? undefined : convTime || undefined,
        date_unknown: dateUnknown || undefined,
      });
      toast.success("Historical conversation imported.");
      onImported?.(data.conversation);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.duplicate) {
        submittedRef.current = false;
        if (window.confirm(`${d.message}\n\nExisting: "${d.existing_title}". Import anyway?`)) {
          return doImport(true);
        }
      } else {
        submittedRef.current = false;
        toast.error(typeof d === "string" ? d : "Import failed.");
      }
    } finally { setBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("File too large (max 5MB for text import).");
    const reader = new FileReader();
    reader.onload = () => { setRaw(String(reader.result || "")); if (!title) setTitle(f.name.replace(/\.[^.]+$/, "")); toast.success(`Loaded ${f.name}`); };
    reader.onerror = () => toast.error("Could not read the file. For PDFs, paste the text instead in Phase 2.");
    reader.readAsText(f);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto tf-scroll" data-testid="hc-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icons.MailPlus className="h-5 w-5 text-charcoal" />Import Historical Conversation</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 mb-3" data-testid="hc-wizard-steps">
          {["Source", "Content", "Preview", "Import"].map((s, i) => (
            <span key={s} className={`px-2 py-0.5 rounded-full ${step === i + 1 ? "bg-charcoal text-white" : step > i + 1 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100"}`}>{i + 1}. {s}</span>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-2" data-testid="hc-step-source">
            {SOURCES.map((s) => {
              const Ic = Icons[s.icon] || Icons.Mail;
              return (
                <button key={s.key} onClick={() => setSourceType(s.key)} data-testid={`hc-source-${s.key}`}
                  className={`w-full flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${sourceType === s.key ? "border-charcoal bg-slate-50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <Ic className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
                  <div><div className="text-sm font-semibold text-slate-800">{s.label}</div><div className="text-xs text-slate-500">{s.hint}</div></div>
                  {sourceType === s.key && <Icons.Check className="h-4 w-4 text-charcoal ml-auto" />}
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="hc-step-content">
            {sourceType === "plain_text" && (
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="hc-chronology">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Icons.CalendarClock className="h-3.5 w-3.5" />Conversation chronology</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Conversation Date <span className="text-rose-500">*</span></label>
                    <input type="date" value={convDate} disabled={dateUnknown} onChange={(e) => setConvDate(e.target.value)} data-testid="hc-conv-date"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Conversation Time <span className="text-slate-400">(optional)</span></label>
                    <input type="time" value={convTime} disabled={dateUnknown} onChange={(e) => setConvTime(e.target.value)} data-testid="hc-conv-time"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="hc-date-unknown">
                  <input type="checkbox" checked={dateUnknown} onChange={(e) => setDateUnknown(e.target.checked)} className="rounded border-slate-300" />
                  Date Unknown (legacy import — kept as lower-confidence evidence)
                </label>
                <p className="text-[11px] text-slate-400">Allison uses chronology to order the conversation and detect the latest client decision. A time is optional; when omitted the date is stored with date-only precision.</p>
              </div>
            )}
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-500">Paste the conversation ({SOURCE_LABEL[sourceType]})</label>
              <div>
                <input ref={fileRef} type="file" accept=".txt,.eml,.md,.csv,.text" className="hidden" onChange={onFile} data-testid="hc-file-input" />
                <button onClick={() => fileRef.current?.click()} className="text-xs inline-flex items-center gap-1 text-slate-600 hover:text-charcoal" data-testid="hc-upload-btn"><Icons.Upload className="h-3.5 w-3.5" />Upload text file</button>
              </div>
            </div>
            <textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={9} data-testid="hc-raw-input"
              placeholder="Paste the full email thread or correspondence here…"
              className="w-full rounded-lg border border-slate-300 p-3 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" />
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional — inferred from subject)" data-testid="hc-title-input"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" />
              <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags, comma separated (optional)" data-testid="hc-tags-input"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" />
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500"><Icons.ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />Imported as evidence with default visibility <b>Staff only</b>. The complete original will be retained.</div>
          </div>
        )}

        {step === 3 && preview && (
          <div className="space-y-3" data-testid="hc-step-preview">
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <Pill tone={PARSE_TONE[preview.parse_status]}>{preview.parse_status.replace(/_/g, " ")}</Pill>
              <span className="text-slate-500">{preview.message_count} message(s) · {preview.participants.length} participant(s)</span>
            </div>
            {preview.parse_status !== "parsed" && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2" data-testid="hc-parse-notice">
                <Icons.Info className="h-4 w-4 shrink-0 mt-0.5" />Some message details could not be identified reliably. The complete original conversation will be preserved.
              </div>
            )}
            <div className="max-h-64 overflow-y-auto tf-scroll space-y-2 rounded-lg border border-slate-100 p-2">
              {preview.messages.map((m, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`hc-preview-msg-${i}`}>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <Pill tone={m.direction === "incoming" ? "info" : m.direction === "outgoing" ? "success" : "neutral"}>{m.direction}</Pill>
                    <span className="font-medium text-slate-700">{m.sender || m.email || "Unknown sender"}</span>
                    <span className="text-slate-400">· {m.timestamp_display || "Unknown"}</span>
                  </div>
                  {m.subject && <div className="text-xs font-semibold text-slate-600 mt-1">{m.subject}</div>}
                  <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap line-clamp-4">{m.body}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400">To correct sender/date/message details, cancel and adjust the pasted source — parsed evidence cannot be hand-edited.</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step > 1 && step < 4 && <button onClick={() => setStep(step - 1)} className="text-sm px-3 py-2 rounded-lg border border-slate-200" data-testid="hc-wizard-back">Back</button>}
          {step === 1 && <ActionButton onClick={() => setStep(2)} data-testid="hc-wizard-next">Next</ActionButton>}
          {step === 2 && <ActionButton onClick={runPreview} loading={busy} data-testid="hc-wizard-preview">Preview</ActionButton>}
          {step === 3 && <ActionButton icon="Check" onClick={() => doImport(false)} loading={busy} data-testid="hc-wizard-import">Import Conversation</ActionButton>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Conversation Viewer ───────────────────────── */
function HCDiagnostics({ d }) {
  const [open, setOpen] = useState(false);
  if (!d) return null;
  const yn = (b) => (b ? "Yes" : "No");
  const tone = (ok) => (ok ? "text-emerald-700" : "text-amber-700");
  const Row = ({ label, value, ok }) => (
    <div className="flex items-center justify-between gap-3 py-0.5">
      <span className="text-slate-400">{label}</span>
      <span className={`font-medium ${ok === undefined ? "text-slate-700" : tone(ok)}`} data-testid={`hc-diag-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{value}</span>
    </div>
  );
  const imp = d.import_status || {}, lk = d.linkage || {}, av = d.ai_availability || {}, ch = d.chronology || {}, de = d.decision_extraction || {};
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden" data-testid="hc-diagnostics">
      <button onClick={() => setOpen((s) => !s)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-600" data-testid="hc-diag-toggle">
        <span className="inline-flex items-center gap-1.5"><Icons.Stethoscope className="h-3.5 w-3.5" />Diagnostics (staff only)</span>
        <Icons.ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-[12px]">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Import Status</div>
            <Row label="Raw Content Present" value={yn(imp.raw_content_present)} ok={imp.raw_content_present} />
            <Row label="Parsing Status" value={imp.parsing_status} ok={imp.parsing_status === "Complete"} />
            <Row label="Parsed Messages" value={imp.parsed_messages} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Linkage</div>
            <Row label="Client Linkage" value={lk.client_linkage} ok={lk.client_linkage === "Confirmed"} />
            <Row label="Conversation Thread" value={lk.conversation_thread} ok={lk.conversation_thread === "Confirmed"} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">AI Availability</div>
            <Row label="Case Context" value={yn(av.case_context)} ok={av.case_context} />
            <Row label="AI Briefing" value={yn(av.ai_briefing)} ok={av.ai_briefing} />
            <Row label="Command Card" value={yn(av.command_card)} ok={av.command_card} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Chronology</div>
            <Row label="Chronology Source" value={(ch.chronology_source || "unknown").replace(/_/g, " ")} ok={ch.chronology_source && ch.chronology_source !== "unknown"} />
            <Row label="Reliability" value={ch.reliability} ok={ch.reliability === "High"} />
            <Row label="Conversation Date" value={ch.conversation_date || "Unknown"} />
            <Row label="Latest Message Date" value={ch.latest_message_date} />
            <Row label="Latest Client Decision" value={ch.latest_client_decision} />
          </div>
          <div className="sm:col-span-2 border-t border-slate-200 pt-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Decision Extraction</div>
            <Row label="Latest Client Intent" value={de.latest_client_intent} ok={de.latest_client_intent && de.latest_client_intent !== "None detected"} />
            {de.source && <Row label="Source" value={de.source} />}
            <Row label="Consumed by Case Context" value={yn(de.consumed_by_case_context)} ok={de.consumed_by_case_context} />
            {de.confidence && <Row label="Confidence" value={de.confidence} />}
          </div>
        </div>
      )}
    </div>
  );
}

function Viewer({ convId, clientId, onClose, onChanged }) {
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [tab, setTab] = useState("parsed");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [titleEdit, setTitleEdit] = useState(null);
  const [tagsEdit, setTagsEdit] = useState(null);
  const [docs, setDocs] = useState([]);
  const [picker, setPicker] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/historical-conversations/${convId}`); setConv(data); return data; }
    catch { toast.error("Could not load conversation."); }
  }, [convId]);
  useEffect(() => { load(); }, [load]);
  // Auto-poll while Allison's analysis is still generating (§1 — generated once, shortly after import).
  useEffect(() => {
    if (conv?.ai_status !== "pending") return;
    const t = setInterval(async () => { const d = await load(); if (d?.ai_status !== "pending") clearInterval(t); }, 5000);
    return () => clearInterval(t);
  }, [conv?.ai_status, load]);

  const [regenerating, setRegenerating] = useState(false);
  const regenerate = async () => {
    setRegenerating(true);
    try { await api.post(`/historical-conversations/${convId}/analyze`); await load(); toast.success("Allison re-analyzed the conversation."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not analyze."); }
    finally { setRegenerating(false); }
  };

  const saveMeta = async (patch) => {
    setBusy(true);
    try { await api.patch(`/historical-conversations/${convId}`, patch); await load(); onChanged?.(); toast.success("Saved."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); setTitleEdit(null); setTagsEdit(null); }
  };
  const setVisibility = async (visibility) => {
    setBusy(true);
    try { await api.patch(`/historical-conversations/${convId}/visibility`, { visibility }); await load(); onChanged?.(); toast.success(`Visibility → ${visibility.replace(/_/g, " ")}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not change visibility."); }
    finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await api.post(`/historical-conversations/${convId}/notes`, { note }); setNote(""); await load(); toast.success("Note added."); }
    catch { toast.error("Could not add note."); } finally { setBusy(false); }
  };
  const openPicker = async () => {
    try { const { data } = await api.get(`/files`, { params: { client_id: clientId } }); setDocs(data.items || data.files || data || []); setPicker(true); }
    catch { toast.error("Could not load documents."); }
  };
  const linkDoc = async (document_id) => {
    setBusy(true);
    try { await api.post(`/historical-conversations/${convId}/attachments`, { document_id }); await load(); onChanged?.(); toast.success("Document linked."); setPicker(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not link."); } finally { setBusy(false); }
  };
  const unlinkDoc = async (document_id) => {
    setBusy(true);
    try { await api.delete(`/historical-conversations/${convId}/attachments/${document_id}`); await load(); onChanged?.(); toast.success("Unlinked (document retained)."); }
    catch { toast.error("Could not unlink."); } finally { setBusy(false); }
  };
  // Attachments become FIRST-CLASS canonical documents (canonical pipeline), then linked to the conversation.
  const hcUploadFn = useCallback((file, { signal, asVersion, separate, note, onProgress }) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("destination_type", "client"); fd.append("destination_id", clientId);
    fd.append("historical_conversation_id", convId);
    fd.append("category", "historical_conversation");
    if (asVersion) fd.append("as_version", "true");
    if (separate) fd.append("separate", "true");
    if (note) fd.append("version_note", note);
    return api.post("/documents-intel/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" }, signal,
      onUploadProgress: (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    }).then((r) => r.data).catch((err) => {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "duplicate_document") {
        const e2 = new Error("Already uploaded — keep as a new version?");
        e2.duplicate = detail.existing; throw e2;
      }
      throw new Error(typeof detail === "string" ? detail : "Upload failed.");
    });
  }, [clientId, convId]);
  const hcPollStatus = useCallback((id) => api.get(`/documents-intel/${id}/status`).then((r) => r.data), []);

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto tf-scroll p-5" data-testid="hc-viewer">
        {!conv ? <div className="py-12 text-center text-slate-400">Loading…</div> : (
          <>
            <SheetHeader className="mb-3">
              <SheetTitle className="flex items-center gap-2 pr-8">
                <Icons.Archive className="h-5 w-5 text-charcoal shrink-0" />
                {titleEdit === null ? (
                  <button onClick={() => setTitleEdit(conv.title)} className="text-left hover:underline" data-testid="hc-view-title">{conv.title}</button>
                ) : (
                  <span className="flex items-center gap-1 flex-1">
                    <input value={titleEdit} onChange={(e) => setTitleEdit(e.target.value)} className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" data-testid="hc-title-edit" />
                    <button onClick={() => saveMeta({ title: titleEdit })} disabled={busy} className="text-emerald-600" data-testid="hc-title-save"><Icons.Check className="h-4 w-4" /></button>
                    <button onClick={() => setTitleEdit(null)} className="text-slate-400"><Icons.X className="h-4 w-4" /></button>
                  </span>
                )}
              </SheetTitle>
            </SheetHeader>

            <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
              <Pill tone="neutral">{SOURCE_LABEL[conv.source_type] || conv.source_type}</Pill>
              <Pill tone={PARSE_TONE[conv.parse_status]}>{conv.parse_status.replace(/_/g, " ")}</Pill>
              <button onClick={() => setVisibility(conv.visibility === "staff_only" ? "client_visible" : "staff_only")} disabled={busy} data-testid="hc-visibility-toggle"
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border ${conv.visibility === "client_visible" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                {conv.visibility === "client_visible" ? <Icons.Eye className="h-3 w-3" /> : <Icons.Lock className="h-3 w-3" />}{conv.visibility.replace(/_/g, " ")}
              </button>
              <span className="text-slate-400">{conv.message_count} msg · imported {fmtDate(conv.imported_at)} by {conv.imported_by_name || "staff"}</span>
            </div>

            {conv.diagnostics && <HCDiagnostics d={conv.diagnostics} />}

            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden mb-3">
              {[["parsed", "Parsed conversation", "MessagesSquare"], ["original", "Original source", "FileText"]].map(([k, l, ic]) => {
                const Ic = Icons[ic];
                return <button key={k} onClick={() => setTab(k)} data-testid={`hc-tab-${k}`}
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 ${tab === k ? "bg-charcoal text-white" : "text-slate-600 hover:bg-slate-50"}`}><Ic className="h-3.5 w-3.5" />{l}</button>;
              })}
            </div>

            {tab === "parsed" ? (
              <div className="space-y-2" data-testid="hc-parsed-view">
                {conv.parse_status !== "parsed" && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
                    <Icons.Info className="h-4 w-4 shrink-0 mt-0.5" />Some message details could not be identified reliably. The complete original conversation is preserved under “Original source”.
                  </div>
                )}
                {(conv.messages || []).map((m) => (
                  <div key={m.id} className="rounded-lg border border-slate-200 p-3" data-testid={`hc-msg-${m.id}`}>
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <Pill tone={m.direction === "incoming" ? "info" : m.direction === "outgoing" ? "success" : "neutral"}>{m.direction}</Pill>
                      <span className="font-medium text-slate-700">{m.sender || m.email || "Unknown sender"}</span>
                      {m.email && m.sender && <span className="text-slate-400">&lt;{m.email}&gt;</span>}
                      <span className="text-slate-400 ml-auto">{m.timestamp_display || "Date unavailable"}</span>
                    </div>
                    {m.subject && <div className="text-sm font-semibold text-slate-700 mt-1">{m.subject}</div>}
                    <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap break-words">{m.body}</div>
                  </div>
                ))}
              </div>
            ) : (
              <pre className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs whitespace-pre-wrap break-words max-h-[50vh] overflow-y-auto tf-scroll" data-testid="hc-original-view">{conv.raw_content}</pre>
            )}

            {/* Allison — Conversation Summary, Extracted Knowledge, Ask Allison (Phase 3, advisory) */}
            <AllisonBlock conv={conv} onRegenerate={regenerate} regenerating={regenerating} />

            {/* Attachments */}
            <div className="mt-5 border-t border-slate-100 pt-4" data-testid="hc-attachments">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Icons.Paperclip className="h-3.5 w-3.5" />Attachments (Document Library)</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowUpload((s) => !s)} className="text-xs inline-flex items-center gap-1 text-slate-600 hover:text-charcoal" data-testid="hc-attach-upload">
                    <Icons.Upload className="h-3.5 w-3.5" />Upload
                  </button>
                  <button onClick={openPicker} className="text-xs inline-flex items-center gap-1 text-slate-600 hover:text-charcoal" data-testid="hc-attach-link"><Icons.Link2 className="h-3.5 w-3.5" />Link existing</button>
                </div>
              </div>
              {showUpload && (
                <div className="mb-3" data-testid="hc-attach-uploader">
                  <CanonicalUploader compact uploadFn={hcUploadFn} pollStatus={hcPollStatus} onFileUploaded={(data) => { linkDoc(data.id); }} testId="hc-uploader" />
                </div>
              )}
              {(conv.attachments || []).length === 0 ? <p className="text-xs text-slate-400">No documents linked.</p> : (
                <ul className="space-y-1">
                  {conv.attachments.map((a) => (
                    <li key={a.document_id} className="flex items-center gap-2 text-sm" data-testid={`hc-att-${a.document_id}`}>
                      <Icons.FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="truncate flex-1 text-slate-700">{a.filename || a.document_id}</span>
                      {a.document_type && <span className="text-[10px] bg-slate-100 rounded px-1">{a.document_type}</span>}
                      {/* UC-2: navigate to canonical Document record */}
                      <button
                        onClick={() => navigate(`/documents/${a.document_id}`)}
                        className="text-[11px] text-blue-600 hover:underline shrink-0 inline-flex items-center gap-0.5"
                        title="View in Document Library"
                        data-testid={`hc-att-view-${a.document_id}`}
                      ><Icons.ExternalLink className="h-3 w-3" />View</button>
                      <button onClick={() => unlinkDoc(a.document_id)} disabled={busy} className="text-slate-400 hover:text-rose-600" title="Unlink (keeps document)" data-testid={`hc-unlink-${a.document_id}`}><Icons.X className="h-3.5 w-3.5" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Tags */}
            <div className="mt-4 border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Tags</div>
                {tagsEdit === null && <button onClick={() => setTagsEdit((conv.tags || []).join(", "))} className="text-xs text-slate-500 hover:text-charcoal" data-testid="hc-tags-edit-btn">Edit</button>}
              </div>
              {tagsEdit === null ? (
                <div className="flex flex-wrap gap-1.5">{(conv.tags || []).length ? conv.tags.map((t) => <Pill key={t} tone="neutral">{t}</Pill>) : <span className="text-xs text-slate-400">No tags.</span>}</div>
              ) : (
                <div className="flex items-center gap-2">
                  <input value={tagsEdit} onChange={(e) => setTagsEdit(e.target.value)} className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm" data-testid="hc-tags-edit" placeholder="comma, separated, tags" />
                  <button onClick={() => saveMeta({ tags: tagsEdit.split(",").map((t) => t.trim()).filter(Boolean) })} disabled={busy} className="text-emerald-600" data-testid="hc-tags-save"><Icons.Check className="h-4 w-4" /></button>
                  <button onClick={() => setTagsEdit(null)} className="text-slate-400"><Icons.X className="h-4 w-4" /></button>
                </div>
              )}
            </div>

            {/* Internal notes (staff only) */}
            <div className="mt-4 border-t border-slate-100 pt-4" data-testid="hc-notes">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5"><Icons.Lock className="h-3.5 w-3.5" />Internal notes (staff only)</div>
              <div className="space-y-1.5 mb-2">
                {(conv.internal_notes || []).map((n) => (
                  <div key={n.id} className="rounded-lg bg-amber-50 border border-amber-100 px-2.5 py-1.5 text-xs text-slate-700"><span className="font-medium">{n.by_name || "Staff"}</span> · {fmtDT(n.at)}<div>{n.note}</div></div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" data-testid="hc-note-input"
                  className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" />
                <ActionButton icon="Plus" variant="secondary" onClick={addNote} loading={busy} data-testid="hc-note-add">Add</ActionButton>
              </div>
            </div>
          </>
        )}

        {picker && (
          <Dialog open onOpenChange={() => setPicker(false)}>
            <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto tf-scroll" data-testid="hc-doc-picker">
              <DialogHeader><DialogTitle>Link an existing document</DialogTitle></DialogHeader>
              {docs.length === 0 ? <EmptyState icon="FileText" title="No documents" description="This client has no documents yet. Use Upload instead." /> : (
                <ul className="space-y-1">
                  {docs.map((d) => (
                    <li key={d.id}>
                      <button onClick={() => linkDoc(d.id)} disabled={busy} className="w-full flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-left text-sm hover:bg-slate-50" data-testid={`hc-pick-${d.id}`}>
                        <Icons.FileText className="h-4 w-4 text-slate-400 shrink-0" />
                        <span className="truncate flex-1">{d.original_filename || d.title || d.document_type || "Document"}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </DialogContent>
          </Dialog>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ───────────────────────── Allison AI block (Phase 3, advisory) ───────────────────────── */
const CAT_LABEL = {
  requested_service: "Requested service", country: "Country", tax_residency: "Tax residency",
  citizenship: "Citizenship", foreign_corporation: "Foreign corporation", foreign_partnership: "Foreign partnership",
  foreign_trust: "Foreign trust", rental_property: "Rental property", employment: "Employment",
  self_employment: "Self-employment", crypto: "Crypto", retirement_income: "Retirement income",
  prior_filing_years: "Prior filing years", delinquent_returns: "Delinquent returns", fbar_indicator: "FBAR indicator",
  fatca_indicator: "FATCA indicator", streamlined_indicator: "Streamlined indicator", thai_tax_indicator: "Thai tax indicator",
  planning_opportunity: "Planning opportunity", compliance_risk: "Compliance risk",
};
const confTone = (c) => (c >= 80 ? "success" : c >= 55 ? "warning" : "neutral");

const ASK_PROMPTS = [
  "Summarize this conversation.", "What services should we recommend?", "What tax issues were identified?",
  "What documents should we request?", "What follow-up questions remain?", "Prepare me for the next meeting.", "Draft a response.",
];

function AllisonBlock({ conv, onRegenerate, regenerating }) {
  const s = conv.ai_summary;
  const findings = conv.ai_findings || [];
  const status = conv.ai_status;
  const [asking, setAsking] = useState(false);
  const [qa, setQa] = useState([]);        // {q, a}
  const [q, setQ] = useState("");
  const sessionId = `hc-${conv.id}`;

  const askAllison = async (question) => {
    const text = (question || q).trim();
    if (!text) return;
    setAsking(true); setQ("");
    // Reuse the EXISTING Allison chat endpoint — conversation supplied as read-only context (no new AI service).
    const ctxLines = [`Historical conversation "${conv.title}" (${conv.source_type}).`];
    if (s?.overview) ctxLines.push(`Summary: ${s.overview}`);
    (conv.messages || []).forEach((m) => ctxLines.push(`[Msg ${m.order}] ${m.direction} ${m.sender || m.email || ""} (${m.timestamp_display || "date unknown"}): ${(m.body || "").slice(0, 600)}`));
    try {
      const { data } = await api.post(`/ai-agents/allison/chat`, { message: text, session_id: sessionId, context: ctxLines.join("\n").slice(0, 6000) });
      setQa((prev) => [...prev, { q: text, a: data.reply || "(no reply)" }]);
    } catch (e) { toast.error(e.response?.data?.detail || "Allison is unavailable right now."); }
    finally { setAsking(false); }
  };

  return (
    <div className="mt-5 border-t border-slate-100 pt-4 space-y-4" data-testid="hc-allison">
      {/* Conversation Summary */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Icons.Sparkles className="h-3.5 w-3.5 text-brand-gold-dark" />Allison — Conversation Summary</div>
          <button onClick={onRegenerate} disabled={regenerating} className="text-xs text-slate-500 hover:text-charcoal inline-flex items-center gap-1" data-testid="hc-ai-regenerate">
            <Icons.RefreshCw className={`h-3.5 w-3.5 ${regenerating ? "animate-spin" : ""}`} />Regenerate
          </button>
        </div>
        {status === "pending" ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-2" data-testid="hc-ai-pending"><Icons.Loader2 className="h-4 w-4 animate-spin" />Allison is analyzing this conversation…</div>
        ) : status === "unavailable" ? (
          <p className="text-xs text-slate-400" data-testid="hc-ai-unavailable">AI analysis unavailable. Use Regenerate to try again.</p>
        ) : s ? (
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-sm space-y-2" data-testid="hc-ai-summary">
            {s.overview && <p className="text-slate-700">{s.overview}</p>}
            {[["Services discussed", s.services_discussed], ["Outstanding questions", s.outstanding_questions],
              ["Client concerns", s.client_concerns], ["Deadlines", s.deadlines], ["Follow-ups", s.follow_up_items]]
              .filter(([, v]) => (v || []).length).map(([label, v]) => (
                <div key={label}>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
                  <ul className="list-disc list-inside text-slate-600 text-[13px]">{v.map((x, i) => <li key={i}>{x}</li>)}</ul>
                </div>
              ))}
            <p className="text-[10px] text-slate-400 italic pt-1 border-t border-slate-200">AI-generated summary · the original conversation remains the authoritative evidence.</p>
          </div>
        ) : <p className="text-xs text-slate-400">No summary yet.</p>}
      </div>

      {/* Extracted Knowledge (advisory findings with citations) */}
      {findings.length > 0 && (
        <div data-testid="hc-ai-findings">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Extracted Knowledge <span className="font-normal normal-case">— advisory only, nothing is applied automatically</span></div>
          <div className="space-y-1.5">
            {findings.map((f, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`hc-finding-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Pill tone="info" className="!text-[10px]">{CAT_LABEL[f.category] || f.category}</Pill>
                  <span className="text-sm font-medium text-slate-800">{f.label}{f.value ? `: ${f.value}` : ""}</span>
                  <Pill tone={confTone(f.confidence)} className="!text-[10px] ml-auto">{f.confidence}% confidence</Pill>
                </div>
                {f.reasoning && <p className="text-xs text-slate-600 mt-1">{f.reasoning}</p>}
                <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1" data-testid={`hc-finding-cite-${i}`}>
                  <Icons.Quote className="h-3 w-3" />Evidence: this conversation
                  {f.evidence?.message_index != null && ` · Message ${f.evidence.message_index}`}
                  {f.evidence?.date && f.evidence.date !== "Date unavailable" && ` · ${f.evidence.date}`}
                  {f.evidence?.subject && ` · "${f.evidence.subject}"`}
                  {f.evidence?.quote && <span className="italic text-slate-500">— “{f.evidence.quote}”</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ask Allison — reuses the existing Allison interface */}
      <div data-testid="hc-ask-allison">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5"><Icons.MessageCircle className="h-3.5 w-3.5" />Ask Allison About This Conversation</div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {ASK_PROMPTS.map((p) => (
            <button key={p} onClick={() => askAllison(p)} disabled={asking} data-testid="hc-ask-chip"
              className="text-[11px] rounded-full border border-slate-200 px-2.5 py-1 text-slate-600 hover:bg-slate-50 hover:border-brand-gold">{p}</button>
          ))}
        </div>
        {qa.length > 0 && (
          <div className="space-y-2 mb-2">
            {qa.map((x, i) => (
              <div key={i} className="rounded-lg border border-slate-100 p-2.5" data-testid={`hc-qa-${i}`}>
                <div className="text-xs font-semibold text-slate-700 flex items-center gap-1"><Icons.CornerDownRight className="h-3 w-3 text-slate-400" />{x.q}</div>
                <div className="text-[13px] text-slate-600 mt-1 whitespace-pre-wrap">{x.a}</div>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askAllison()} data-testid="hc-ask-input"
            placeholder="Ask a question about this conversation…" className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" />
          <ActionButton icon="Send" variant="secondary" onClick={() => askAllison()} loading={asking} data-testid="hc-ask-send">Ask</ActionButton>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Card + Section ───────────────────────── */
function ConvCard({ conv, onView }) {
  const firstDate = (conv.messages || [])[0]?.timestamp_display;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 hover:border-slate-300 transition-colors" data-testid={`hc-card-${conv.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800 truncate">{conv.title}</span>
            <Pill tone="neutral" className="!text-[10px]">{SOURCE_LABEL[conv.source_type] || conv.source_type}</Pill>
            <Pill tone={PARSE_TONE[conv.parse_status]} className="!text-[10px]">{conv.parse_status.replace(/_/g, " ")}</Pill>
            <span className={`inline-flex items-center gap-0.5 text-[10px] rounded-full px-1.5 py-0.5 border ${conv.visibility === "client_visible" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
              {conv.visibility === "client_visible" ? <Icons.Eye className="h-2.5 w-2.5" /> : <Icons.Lock className="h-2.5 w-2.5" />}{conv.visibility.replace(/_/g, " ")}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
            <span>{(conv.participants || []).slice(0, 3).map((p) => p.name || p.email).filter(Boolean).join(", ") || "Participants unknown"}</span>
            <span>· {conv.message_count} message(s)</span>
            <span>· {firstDate && firstDate !== "Unknown" ? firstDate : "Date unavailable"}</span>
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
            <span>Imported {fmtDate(conv.imported_at)} by {conv.imported_by_name || "staff"}</span>
            {(conv.tags || []).length > 0 && <span>· {conv.tags.map((t) => `#${t}`).join(" ")}</span>}
            {conv.has_attachments && <span className="inline-flex items-center gap-0.5"><Icons.Paperclip className="h-3 w-3" />{(conv.attachments || []).length}</span>}
          </div>
        </div>
        <ActionButton icon="Eye" variant="secondary" onClick={() => onView(conv.id)} data-testid={`hc-view-${conv.id}`}>View</ActionButton>
      </div>
    </div>
  );
}

export default function HistoricalConversations({ clientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizard, setWizard] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/historical-conversations/client/${clientId}`); setItems(data.conversations || []); }
    catch { /* silent — section simply shows empty */ } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const doSearch = async () => {
    if (q.trim().length < 2) { setResults(null); return; }
    try { const { data } = await api.get(`/historical-conversations/search`, { params: { client_id: clientId, q } }); setResults(data.results || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Search failed."); }
  };

  const shown = results !== null
    ? items.filter((c) => results.some((r) => r.id === c.id))
    : items;

  return (
    <SectionCard title="Historical Conversations" icon="Archive"
      action={<ActionButton icon="MailPlus" variant="secondary" onClick={() => setWizard(true)} data-testid="hc-import-btn">Import Historical Conversation</ActionButton>}>
      <p className="text-xs text-slate-400 -mt-2 mb-3">Pre-engagement correspondence kept as read-only evidence — separate from the live conversation above.</p>

      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1">
          <Icons.Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()} data-testid="hc-search-input"
            placeholder="Search historical conversations…" aria-label="Search historical conversations"
            className="w-full rounded-lg border border-slate-300 pl-8 pr-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" />
        </div>
        {results !== null && <button onClick={() => { setQ(""); setResults(null); }} className="text-xs text-slate-500" data-testid="hc-search-clear">Clear</button>}
        <ActionButton icon="Search" variant="secondary" onClick={doSearch} data-testid="hc-search-btn">Search</ActionButton>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />)}</div>
      ) : shown.length === 0 ? (
        <EmptyState icon="Archive" title={results !== null ? "No matches" : "No historical conversations"}
          description={results !== null ? "Try a different search term." : "Import a pre-engagement email thread or correspondence so Allison can use it as evidence later."}
          action={results === null && <ActionButton icon="MailPlus" onClick={() => setWizard(true)} data-testid="hc-empty-import">Import Historical Conversation</ActionButton>} />
      ) : (
        <div className="space-y-2" data-testid="hc-list">{shown.map((c) => <ConvCard key={c.id} conv={c} onView={setViewId} />)}</div>
      )}

      {wizard && <ImportWizard clientId={clientId} onClose={() => setWizard(false)} onImported={() => load()} />}
      {viewId && <Viewer convId={viewId} clientId={clientId} onClose={() => setViewId(null)} onChanged={load} />}
    </SectionCard>
  );
}
